// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import NotificationErrorCircle from "@synd/XDL/Icons/NotificationErrorCircle";
import NotificationInfoCircle from "@synd/XDL/Icons/NotificationInfoCircle";
import NotificationWarningTriangle from "@synd/XDL/Icons/NotificationWarningTriangle";
import { NamedExoticComponent } from "react";

export type CartMessages = {
    icon: NamedExoticComponent<any>;
    message: string;
    color: string;
    type: "success" | "danger" | "warning" | "info";
}[];
export const getCartLineErrorMessages = (cartLines: CartLineModel[], currentCartLine: CartLineModel, adjustedCartLineId?: string) => {
    const errorMessages: CartMessages = [];
    const sumQtyPerUom = cartLines?.reduce((sum, current) => {
        return current.productId === currentCartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    // Condition same as CartLineCardExpanded.tsx
    if (currentCartLine.hasInsufficientInventory && !isOutOfStock(currentCartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            currentCartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        ).toString();
        errorMessages.push({
            icon: NotificationWarningTriangle,
            color: "warning.main",
            message: tooManyRequestedMessage,
            type: "warning",
        });
    }

    if (currentCartLine.isRestricted) {
        errorMessages.push({
            icon: NotificationErrorCircle,
            color: "danger.main",
            message: translate("Restricted product"),
            type: "danger",
        });
    }

    if (!currentCartLine.isActive) {
        errorMessages.push({
            icon: NotificationErrorCircle,
            color: "danger.main",
            message: translate("Inactive Product"),
            type: "danger",
        });
    }

    if (currentCartLine.cantBuy) {
        errorMessages.push({
            icon: NotificationWarningTriangle,
            color: "warning.main",
            message: translate("View-only product"),
            type: "warning",
        });
    }

    if (currentCartLine.id === adjustedCartLineId) {
        errorMessages.push({
            icon: NotificationInfoCircle,
            color: "warning.main",
            message: siteMessage("Cart_QuantityAdjusted").toString(),
            type: "warning",
        });
    }

    if ((currentCartLine as any).upcFieldIsMissing) {
        errorMessages.push({
            icon: NotificationWarningTriangle,
            color: "warning.main",
            message: siteMessage("Custom_UPC_Field_Missing").toString(),
            type: "warning",
        });
    }

    return errorMessages;
};
